import type { EnemyView } from "./enemyView";

/**
 * The rare spawns' own clips (art/unitkit/README.md "Rare spawns", v2 2026-10-21), played by the view — presentation
 * only, nothing here reads or writes the sim:
 *
 * - `spawn` (2.0 s) once as the rare appears at the portal: it bursts up out of the ground, lands and shows off its
 *   set piece (the stag's halo, the tortoise's pearl, the salamander's sail, the golem's chest-beat, the ram's rear,
 *   the toad's booming sac), then the walk takes over;
 * - `roar` (1.5 s) now and then while it walks: every `TauntEvery` seconds, never over another one-shot (an attack,
 *   a hit), never while it is dying, and not once it is below `TauntMinHp` of its health (a killing blow mid-roar
 *   would blend the `die` with the roar's tail: the rig averages one-shots that overlap).
 */
export const RARE_CLIPS = {
  /** Seconds between taunts while it lives (the first one counts from the end of its spawn). */
  TauntEvery: 9,
  /** The fraction of its health under which it stops taunting. */
  TauntMinHp: 0.25,
} as const;

/** The rare appeared: its entrance (a model without the clip just walks). */
export function rareArrive(v: EnemyView): void {
  v.rig.playOnce("spawn", false, null, { solo: true });
}

/** When the living rare roars: a timer that runs while it is alive and free, and fires only when its rig is free. */
export class RareTaunt {
  private wait: number = RARE_CLIPS.TauntEvery;

  reset(): void { this.wait = RARE_CLIPS.TauntEvery; }

  /**
   * Per frame: true when it should roar now (the timer restarts). `busy`: a one-shot plays; `alive`: not dying;
   * `health`: its health fraction (0-1).
   */
  tick(dt: number, busy: boolean, alive: boolean, health = 1): boolean {
    if (!alive || health < RARE_CLIPS.TauntMinHp) return false;
    if (busy) return false;
    this.wait -= dt;
    if (this.wait > 0) return false;
    this.wait = RARE_CLIPS.TauntEvery;
    return true;
  }
}

/** The roar itself (solo: the walk fades under it, and back after). */
export function rareRoar(v: EnemyView): void {
  v.rig.playOnce("roar", false, null, { solo: true });
}
